import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, ButtonGroup, Col, Container, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import { Pencil, Plus, Settings2, Trash2, X } from 'lucide-react';
import { toast } from 'react-toastify';
import Navigation from '../components/Navigation/Navigation';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/apiClient';

type Facet = 'use' | 'source' | 'season' | 'character' | 'forces' | 'occasion';

const FACET_LABEL: Record<Facet, string> = {
  use: 'Use',
  source: 'Source',
  season: 'Season',
  character: 'Character',
  forces: 'Forces',
  occasion: 'Occasion',
};

interface FacetValue {
  id: number;
  facet: Facet;
  value: string;
  sort_order: number;
  is_seed: boolean;
  in_use: number;
}

interface Piece {
  id: number;
  title: string;
  tune: string | null;
  composer: string | null;
  note: string | null;
  facet_value_ids: number[];
}

interface PieceDraft {
  title: string;
  tune: string;
  composer: string;
  note: string;
  facet_value_ids: number[];
}

const emptyDraft = (): PieceDraft => ({ title: '', tune: '', composer: '', note: '', facet_value_ids: [] });

function errorText(err: any, fallback: string): string {
  return err?.response?.data?.error || err?.response?.data?.error?.message || fallback;
}

const PreludesPage: React.FC = () => {
  const { user } = useAuth();
  const canEdit = /admin/i.test(user?.role_name || '');
  const [searchParams, setSearchParams] = useSearchParams();

  const [facets, setFacets] = useState<Facet[]>([]);
  const [values, setValues] = useState<FacetValue[]>([]);
  const [pieces, setPieces] = useState<Piece[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [editing, setEditing] = useState<Piece | null>(null);
  const [draft, setDraft] = useState<PieceDraft>(emptyDraft());
  const [showEditor, setShowEditor] = useState(false);
  const [saving, setSaving] = useState(false);

  const [showVocab, setShowVocab] = useState(false);
  const [newValueFacet, setNewValueFacet] = useState<Facet>('season');
  const [newValueText, setNewValueText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [f, p] = await Promise.all([
        apiClient.get('/preludes/facets'),
        apiClient.get('/preludes/pieces'),
      ]);
      setFacets(f.data.data.facets);
      setValues(f.data.data.values);
      setPieces(p.data.data);
    } catch (err) {
      setLoadError(errorText(err, 'Could not load the list.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Filters live in the URL: ?season=3,4&use=1&group=season&view=grid
  const selected = useMemo(() => {
    const out: Record<Facet, number[]> = { use: [], source: [], season: [], character: [], forces: [], occasion: [] };
    for (const facet of Object.keys(out) as Facet[]) {
      const raw = searchParams.get(facet);
      if (raw) out[facet] = raw.split(',').map(Number).filter(n => n > 0);
    }
    return out;
  }, [searchParams]);
  const groupBy = (searchParams.get('group') || '') as Facet | '';
  const view = searchParams.get('view') === 'grid' ? 'grid' : 'list';
  const search = searchParams.get('q') || '';

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const toggleFilter = (facet: Facet, id: number) => {
    const current = selected[facet];
    const next = current.includes(id) ? current.filter(x => x !== id) : [...current, id];
    setParam(facet, next.join(','));
  };

  const clearFilters = () => {
    const next = new URLSearchParams();
    if (groupBy) next.set('group', groupBy);
    if (view === 'grid') next.set('view', 'grid');
    setSearchParams(next, { replace: true });
  };

  const valuesByFacet = useMemo(() => {
    const map = {} as Record<Facet, FacetValue[]>;
    for (const facet of facets) map[facet] = values.filter(v => v.facet === facet);
    return map;
  }, [facets, values]);
  const valueById = useMemo(() => new Map(values.map(v => [v.id, v])), [values]);
  const usageCount = useMemo(() => {
    const counts = new Map<number, number>();
    for (const piece of pieces) for (const id of piece.facet_value_ids) counts.set(id, (counts.get(id) || 0) + 1);
    return counts;
  }, [pieces]);

  // OR within a facet, AND across facets.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return pieces.filter(piece => {
      if (q) {
        const hay = `${piece.title} ${piece.tune || ''} ${piece.composer || ''} ${piece.note || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      for (const facet of facets) {
        const wanted = selected[facet];
        if (wanted.length && !wanted.some(id => piece.facet_value_ids.includes(id))) return false;
      }
      return true;
    });
  }, [pieces, facets, selected, search]);

  const groups = useMemo(() => {
    if (!groupBy) return [{ label: '', items: filtered }];
    const out: { label: string; items: Piece[] }[] = [];
    for (const v of valuesByFacet[groupBy] || []) {
      const items = filtered.filter(p => p.facet_value_ids.includes(v.id));
      if (items.length) out.push({ label: v.value, items });
    }
    const untagged = filtered.filter(p => !p.facet_value_ids.some(id => valueById.get(id)?.facet === groupBy));
    if (untagged.length) out.push({ label: `No ${FACET_LABEL[groupBy].toLowerCase()} set`, items: untagged });
    return out;
  }, [filtered, groupBy, valuesByFacet, valueById]);

  const activeFilterCount = facets.reduce((n, f) => n + selected[f].length, 0) + (search ? 1 : 0);

  const openNew = () => {
    setEditing(null);
    setDraft(emptyDraft());
    setShowEditor(true);
  };
  const openEdit = (piece: Piece) => {
    setEditing(piece);
    setDraft({
      title: piece.title,
      tune: piece.tune || '',
      composer: piece.composer || '',
      note: piece.note || '',
      facet_value_ids: [...piece.facet_value_ids],
    });
    setShowEditor(true);
  };
  const toggleDraftValue = (id: number) => {
    setDraft(d => ({
      ...d,
      facet_value_ids: d.facet_value_ids.includes(id)
        ? d.facet_value_ids.filter(x => x !== id)
        : [...d.facet_value_ids, id],
    }));
  };

  const savePiece = async () => {
    if (!draft.title.trim()) { toast.error('A title is required.'); return; }
    setSaving(true);
    try {
      if (editing) {
        const r = await apiClient.put(`/preludes/pieces/${editing.id}`, draft);
        setPieces(ps => ps.map(p => (p.id === editing.id ? r.data.data : p)));
      } else {
        const r = await apiClient.post('/preludes/pieces', draft);
        setPieces(ps => [...ps, r.data.data].sort((a, b) => a.title.localeCompare(b.title)));
      }
      setShowEditor(false);
    } catch (err) {
      toast.error(errorText(err, 'Could not save.'));
    } finally {
      setSaving(false);
    }
  };

  const removePiece = async (piece: Piece) => {
    if (!window.confirm(`Delete "${piece.title}" from the list?`)) return;
    try {
      await apiClient.delete(`/preludes/pieces/${piece.id}`);
      setPieces(ps => ps.filter(p => p.id !== piece.id));
      if (editing?.id === piece.id) setShowEditor(false);
    } catch (err) {
      toast.error(errorText(err, 'Could not delete.'));
    }
  };

  // Adding a value always goes into a chosen category.
  const addValue = async (facet: Facet, text: string, selectOnDraft = false) => {
    const value = text.trim();
    if (!value) return;
    try {
      const r = await apiClient.post('/preludes/facets/values', { facet, value });
      const created: FacetValue = r.data.data;
      setValues(vs => (vs.some(v => v.id === created.id) ? vs : [...vs, created]));
      if (selectOnDraft) setDraft(d => ({ ...d, facet_value_ids: [...d.facet_value_ids, created.id] }));
      setNewValueText('');
    } catch (err) {
      toast.error(errorText(err, 'Could not add the value.'));
    }
  };

  const renameValue = async (v: FacetValue) => {
    const next = window.prompt(`Rename "${v.value}" in ${FACET_LABEL[v.facet]}:`, v.value);
    if (!next || next.trim() === v.value) return;
    try {
      const r = await apiClient.put(`/preludes/facets/values/${v.id}`, { value: next.trim() });
      setValues(vs => vs.map(x => (x.id === v.id ? { ...x, value: r.data.data.value } : x)));
    } catch (err) {
      toast.error(errorText(err, 'Could not rename.'));
    }
  };

  const deleteValue = async (v: FacetValue) => {
    const uses = usageCount.get(v.id) || 0;
    const message = uses
      ? `"${v.value}" is on ${uses} piece${uses === 1 ? '' : 's'}. Remove it from them and delete it from ${FACET_LABEL[v.facet]}?`
      : `Remove "${v.value}" from ${FACET_LABEL[v.facet]}?`;
    if (!window.confirm(message)) return;
    try {
      await apiClient.delete(`/preludes/facets/values/${v.id}${uses ? '?force=1' : ''}`);
      setValues(vs => vs.filter(x => x.id !== v.id));
      setPieces(ps => ps.map(p => ({ ...p, facet_value_ids: p.facet_value_ids.filter(id => id !== v.id) })));
    } catch (err) {
      toast.error(errorText(err, 'Could not remove the value.'));
    }
  };

  const tagsFor = (piece: Piece, omit?: Facet) =>
    facets
      .filter(f => f !== omit)
      .flatMap(f => (valuesByFacet[f] || []).filter(v => piece.facet_value_ids.includes(v.id)));

  const renderPiece = (piece: Piece) => (
    <div key={piece.id} className={`prelude-item ${view === 'grid' ? 'prelude-item-grid' : ''}`}>
      <div className="d-flex align-items-baseline justify-content-between gap-2">
        <div className="flex-grow-1">
          <span className="prelude-title">{piece.title}</span>
          {piece.tune && <span className="text-muted"> ({piece.tune})</span>}
          {piece.composer && <span className="text-muted small"> · {piece.composer}</span>}
          {piece.note && <div className="text-muted small">{piece.note}</div>}
        </div>
        {canEdit && (
          <span className="prelude-actions text-nowrap">
            <Button size="sm" variant="link" className="p-0 me-2 text-secondary" onClick={() => openEdit(piece)} title="Edit">
              <Pencil size={15} />
            </Button>
            <Button size="sm" variant="link" className="p-0 text-danger" onClick={() => removePiece(piece)} title="Delete">
              <Trash2 size={15} />
            </Button>
          </span>
        )}
      </div>
      <div className="mt-1">
        {tagsFor(piece, groupBy || undefined).map(v => (
          <Badge
            key={v.id}
            bg="light"
            text="dark"
            className={`me-1 mb-1 fw-normal border prelude-tag prelude-tag-${v.facet}`}
            role="button"
            onClick={() => toggleFilter(v.facet, v.id)}
            title={`Filter by ${FACET_LABEL[v.facet]}: ${v.value}`}
          >
            {v.value}
          </Badge>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <Navigation />
      <Container className="mt-4 mb-5" style={{ maxWidth: 1000 }}>
        <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-3">
          <div>
            <Link to="/prepare" className="text-muted small text-decoration-none">Prepare</Link>
            <h1 className="display-6 mt-1 mb-1">Preludes and Postludes</h1>
            <p className="text-muted mb-0">A reference list. Nothing here is tied to a service.</p>
          </div>
          {canEdit && (
            <div className="d-flex gap-2">
              <Button variant="outline-secondary" size="sm" onClick={() => setShowVocab(true)}>
                <Settings2 size={15} className="me-1" /> Categories
              </Button>
              <Button variant="primary" size="sm" onClick={openNew}>
                <Plus size={15} className="me-1" /> Add piece
              </Button>
            </div>
          )}
        </div>

        {loadError && <Alert variant="danger">{loadError}</Alert>}

        {!loading && (
          <div className="prelude-filters mb-3">
            {facets.map(facet => (
              <div key={facet} className="d-flex flex-wrap align-items-center mb-1">
                <span className="text-muted small me-2 prelude-facet-label">{FACET_LABEL[facet]}</span>
                {(valuesByFacet[facet] || []).map(v => {
                  const on = selected[facet].includes(v.id);
                  return (
                    <Button
                      key={v.id}
                      size="sm"
                      variant={on ? 'dark' : 'outline-secondary'}
                      className="me-1 mb-1 py-0 prelude-chip"
                      onClick={() => toggleFilter(facet, v.id)}
                    >
                      {v.value}
                    </Button>
                  );
                })}
              </div>
            ))}
            <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
              <Form.Control
                size="sm"
                style={{ maxWidth: 220 }}
                placeholder="Search titles, tunes, notes"
                value={search}
                onChange={e => setParam('q', e.target.value)}
              />
              <Form.Select size="sm" style={{ maxWidth: 200 }} value={groupBy} onChange={e => setParam('group', e.target.value)}>
                <option value="">No grouping</option>
                {facets.map(f => <option key={f} value={f}>Group by {FACET_LABEL[f].toLowerCase()}</option>)}
              </Form.Select>
              <ButtonGroup size="sm">
                <Button variant={view === 'list' ? 'secondary' : 'outline-secondary'} onClick={() => setParam('view', '')}>List</Button>
                <Button variant={view === 'grid' ? 'secondary' : 'outline-secondary'} onClick={() => setParam('view', 'grid')}>Grid</Button>
              </ButtonGroup>
              {activeFilterCount > 0 && (
                <Button size="sm" variant="link" className="text-decoration-none" onClick={clearFilters}>
                  <X size={14} /> Clear filters
                </Button>
              )}
              <span className="text-muted small ms-auto">{filtered.length} of {pieces.length}</span>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-5"><Spinner animation="border" /></div>
        ) : filtered.length === 0 ? (
          <p className="text-muted fst-italic">Nothing matches those filters.</p>
        ) : (
          groups.map(group => (
            <section key={group.label || 'all'} className="mb-4">
              {group.label && <h2 className="h5 border-bottom pb-1 mb-2">{group.label}</h2>}
              <div className={view === 'grid' ? 'prelude-grid' : ''}>
                {group.items.map(renderPiece)}
              </div>
            </section>
          ))
        )}
      </Container>

      {/* Piece editor */}
      <Modal show={showEditor} onHide={() => setShowEditor(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editing ? 'Edit piece' : 'Add piece'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-2 mb-3">
            <Col md={6}>
              <Form.Label className="small text-muted mb-0">Title</Form.Label>
              <Form.Control value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })} autoFocus />
            </Col>
            <Col md={3}>
              <Form.Label className="small text-muted mb-0">Tune</Form.Label>
              <Form.Control value={draft.tune} onChange={e => setDraft({ ...draft, tune: e.target.value })} />
            </Col>
            <Col md={3}>
              <Form.Label className="small text-muted mb-0">Composer or arranger</Form.Label>
              <Form.Control value={draft.composer} onChange={e => setDraft({ ...draft, composer: e.target.value })} />
            </Col>
            <Col md={12}>
              <Form.Label className="small text-muted mb-0">Note</Form.Label>
              <Form.Control value={draft.note} onChange={e => setDraft({ ...draft, note: e.target.value })} />
            </Col>
          </Row>
          {facets.map(facet => (
            <div key={facet} className="mb-2">
              <div className="small text-muted mb-1">{FACET_LABEL[facet]}</div>
              <div className="d-flex flex-wrap align-items-center">
                {(valuesByFacet[facet] || []).map(v => {
                  const on = draft.facet_value_ids.includes(v.id);
                  return (
                    <Button
                      key={v.id}
                      size="sm"
                      variant={on ? 'dark' : 'outline-secondary'}
                      className="me-1 mb-1 py-0 prelude-chip"
                      onClick={() => toggleDraftValue(v.id)}
                    >
                      {v.value}
                    </Button>
                  );
                })}
                <InlineAdd label={`Add to ${FACET_LABEL[facet].toLowerCase()}`} onAdd={text => addValue(facet, text, true)} />
              </div>
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <div>
            {editing && (
              <Button variant="outline-danger" size="sm" onClick={() => removePiece(editing)}>
                <Trash2 size={14} className="me-1" /> Delete
              </Button>
            )}
          </div>
          <div>
            <Button variant="secondary" className="me-2" onClick={() => setShowEditor(false)}>Cancel</Button>
            <Button variant="primary" onClick={savePiece} disabled={saving}>{saving ? 'Saving' : 'Save'}</Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Vocabulary manager */}
      <Modal show={showVocab} onHide={() => setShowVocab(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Categories</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small">
            The six categories are fixed. Values within them can be added, renamed, or removed. Removing a value that is still on pieces takes it off those pieces first.
          </p>
          <Form
            className="d-flex flex-wrap gap-2 align-items-center mb-3"
            onSubmit={e => { e.preventDefault(); addValue(newValueFacet, newValueText); }}
          >
            <Form.Select size="sm" style={{ maxWidth: 160 }} value={newValueFacet} onChange={e => setNewValueFacet(e.target.value as Facet)}>
              {facets.map(f => <option key={f} value={f}>{FACET_LABEL[f]}</option>)}
            </Form.Select>
            <Form.Control size="sm" style={{ maxWidth: 260 }} placeholder="New value" value={newValueText} onChange={e => setNewValueText(e.target.value)} />
            <Button size="sm" type="submit" variant="primary"><Plus size={14} className="me-1" /> Add</Button>
          </Form>
          {facets.map(facet => (
            <div key={facet} className="mb-3">
              <div className="fw-semibold small mb-1">{FACET_LABEL[facet]}</div>
              {(valuesByFacet[facet] || []).map(v => (
                <div key={v.id} className="d-flex align-items-center py-1 border-bottom border-light">
                  <span className="flex-grow-1">{v.value}</span>
                  <span className="text-muted small me-3">{(usageCount.get(v.id) || 0) ? `${usageCount.get(v.id)} piece${usageCount.get(v.id) === 1 ? '' : 's'}` : 'unused'}</span>
                  <Button size="sm" variant="link" className="p-0 me-2 text-secondary" onClick={() => renameValue(v)} title="Rename"><Pencil size={14} /></Button>
                  <Button size="sm" variant="link" className="p-0 text-danger" onClick={() => deleteValue(v)} title="Remove"><Trash2 size={14} /></Button>
                </div>
              ))}
            </div>
          ))}
        </Modal.Body>
      </Modal>

      <style dangerouslySetInnerHTML={{ __html: `
        .prelude-facet-label { min-width: 74px; }
        .prelude-chip { font-size: 0.8rem; border-radius: 999px; }
        .prelude-item { padding: 0.45rem 0; border-bottom: 1px solid #f1f3f5; }
        .prelude-item-grid { border: 1px solid #e9ecef; border-radius: 6px; padding: 0.6rem 0.75rem; }
        .prelude-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 0.6rem; }
        .prelude-title { font-weight: 500; }
        .prelude-tag { cursor: pointer; font-size: 0.72rem; }
        .prelude-tag-season { border-color: #b8d8c8 !important; }
        .prelude-tag-character { border-color: #d8c8b8 !important; }
        .prelude-tag-use { border-color: #c8c8d8 !important; }
        .prelude-actions { opacity: 0.45; }
        .prelude-item:hover .prelude-actions { opacity: 1; }
      ` }} />
    </>
  );
};

// Small inline "add a value to this category" control used inside the editor.
const InlineAdd: React.FC<{ label: string; onAdd: (text: string) => void }> = ({ label, onAdd }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  if (!open) {
    return (
      <Button size="sm" variant="link" className="py-0 mb-1 text-decoration-none small" onClick={() => setOpen(true)}>
        <Plus size={13} /> {label}
      </Button>
    );
  }
  return (
    <Form
      className="d-inline-flex align-items-center mb-1"
      onSubmit={e => { e.preventDefault(); onAdd(text); setText(''); setOpen(false); }}
    >
      <Form.Control size="sm" style={{ width: 160 }} autoFocus value={text} onChange={e => setText(e.target.value)} />
      <Button size="sm" type="submit" variant="outline-primary" className="ms-1 py-0">Add</Button>
      <Button size="sm" variant="link" className="py-0 text-secondary" onClick={() => { setOpen(false); setText(''); }}><X size={13} /></Button>
    </Form>
  );
};

export default PreludesPage;
